import { useCallback, useEffect, useState } from "react";

import {
  fetchPushBrokerPolicies,
  setPushPolicy,
  type PushBrokerPolicy,
} from "../../../api/pushBroker";

export interface PushPolicyState {
  /** `${providerId}:${repositoryId}` → push_enabled. Absent key = no policy
   *  row (push hard-disabled orchestrator-side until an operator enables). */
  readonly policies: ReadonlyMap<string, boolean>;
  readonly loading: boolean;
  readonly error: string | null;
  setPolicy: (
    providerId: string,
    repositoryId: string,
    pushEnabled: boolean,
  ) => Promise<void>;
}

export function pushPolicyKey(providerId: string, repositoryId: string) {
  return `${providerId}:${repositoryId}`;
}

/**
 * Per-environment push policy state for the workspace repository list. The
 * policy row is keyed (channel_environment_id, provider_id, repository_id) —
 * exactly the attachment key — so one fetch serves every row in the list.
 * Toggling goes through the orchestrator (fence/mint enforce the result); the
 * badge is an operator affordance, never a credential path.
 */
export function usePushPolicies(
  channelEnvironmentId: string | null,
): PushPolicyState {
  const [policies, setPolicies] = useState<ReadonlyMap<string, boolean>>(
    new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!channelEnvironmentId) {
      setPolicies(new Map());
      return;
    }
    setLoading(true);
    setError(null);
    // Stale-resolution guard: the rail is NOT remounted on env switch, so a
    // slow fetch for env A may resolve after env B's — only the fetch that
    // started for the CURRENT env id may write state.
    const fetchedFor = channelEnvironmentId;
    try {
      const rows: PushBrokerPolicy[] =
        await fetchPushBrokerPolicies(channelEnvironmentId);
      if (fetchedFor !== channelEnvironmentId) {
        return;
      }
      setPolicies(
        new Map(
          rows.map((row) => [
            pushPolicyKey(row.providerId, row.repositoryId),
            row.pushEnabled,
          ]),
        ),
      );
    } catch (caughtError) {
      if (fetchedFor === channelEnvironmentId) {
        setError(
          caughtError instanceof Error
            ? caughtError.message
            : String(caughtError),
        );
      }
    } finally {
      if (fetchedFor === channelEnvironmentId) {
        setLoading(false);
      }
    }
  }, [channelEnvironmentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const setPolicy = useCallback(
    async (providerId: string, repositoryId: string, pushEnabled: boolean) => {
      if (!channelEnvironmentId) {
        return;
      }
      await setPushPolicy({
        channelEnvironmentId,
        providerId,
        pushEnabled,
        repositoryId,
      });
      setPolicies((current) => {
        const next = new Map(current);
        next.set(pushPolicyKey(providerId, repositoryId), pushEnabled);
        return next;
      });
    },
    [channelEnvironmentId],
  );

  return { error, loading, policies, setPolicy };
}
